import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Components as ScreenComponents } from '../../styled';
import { Components } from './styled';
import FeedList from './components/FeedList';
import FeedEditor from './components/FeedEditor';
import AddShortsPanel, { AddPlacement } from './components/AddShortsPanel';
import FeedModal, { FeedModalValues } from './components/FeedModal';
import CsvImportModal from './components/CsvImportModal';
import { ActivityIndicatorContext } from '../../../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../../../providers/ToastProvider/context';
import { isProjectError } from '../../../../utils/errors';
import { getHomeShortsFeeds, setHomeShortsFeeds } from '../../../../api/homeShortsFeeds';
import {
  AccessSegment,
  HomeShortsFeed,
  HomeShortsFeeds,
  ShortNote,
  USER_LEVELS,
} from '../../../../api/homeShortsFeeds/types';
import { getShadowedFeeds, resolveFeed } from '../../../../api/homeShortsFeeds/utils';
import { getAllMentorShorts } from '../../../../api/mentorShort';
import { getAllAuthors } from '../../../../api/author';
import {
  buildCsv,
  createFeedId,
  DEFAULT_FEED_ID,
  downloadCsv,
  EXAMPLE_CSV_FILENAME,
  isNoteEmpty,
  shortsToCsvRows,
  ShortRow,
  slugify,
} from './utils';

const EMPTY_FEEDS: HomeShortsFeeds = { feeds: [], default: { shortIds: [] } };

// Phase 1 ships one ranked list for everyone (Default). Per level/access feeds stay built but hidden
// until needed; the backend ignores `feeds` until then.
const ENABLE_FEED_TARGETING = false;

// Expert card slots on the app's Home feed (alux-backend DEFAULT_LIMITS.experts).
const HOMEPAGE_EXPERT_SLOTS = 4;

// Removes ids that no longer exist (and repeats) from every feed. Reports how many were dropped.
const cleanFeeds = (data: HomeShortsFeeds, validIds: Set<string>) => {
  let removed = 0;
  const clean = (ids: string[]) => {
    const seen = new Set<string>();
    return ids.filter((id) => {
      const keep = validIds.has(id) && !seen.has(id);
      if (keep) {
        seen.add(id);
      } else {
        removed++;
      }
      return keep;
    });
  };
  // Notes for ids that no longer exist are dropped too (not counted in `removed`).
  const notes: Record<string, ShortNote> = {};
  Object.entries(data.notes ?? {}).forEach(([id, note]) => {
    if (validIds.has(id)) {
      notes[id] = note;
    }
  });
  const cleaned: HomeShortsFeeds = {
    feeds: data.feeds.map((feed) => ({ ...feed, shortIds: clean(feed.shortIds || []) })),
    default: { shortIds: clean(data.default.shortIds) },
    notes,
  };
  return { cleaned, removed };
};

const serialize = (data: HomeShortsFeeds) =>
  JSON.stringify({ feeds: data.feeds, default: data.default, notes: data.notes ?? {} });

// Builds ranked homepage short feeds per user segment (level x access) and saves them to
// settings/homeShortsFeeds. Everything stays in a local draft until "Save changes".
const HomepageShorts = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const [shorts, setShorts] = useState<ShortRow[]>([]);
  const [baseline, setBaseline] = useState<HomeShortsFeeds | undefined>(undefined);
  const [draft, setDraft] = useState<HomeShortsFeeds | undefined>(undefined);
  const [removedCount, setRemovedCount] = useState(0);
  const [hasStaleIds, setHasStaleIds] = useState(false);

  const [selectedId, setSelectedId] = useState(DEFAULT_FEED_ID);
  const [previewLevel, setPreviewLevel] = useState<number | ''>('');
  const [previewAccess, setPreviewAccess] = useState<AccessSegment | ''>('');

  const [feedModal, setFeedModal] = useState<{ feedId?: string } | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [addPanel, setAddPanel] = useState<{ onlyUnassigned: boolean } | undefined>(undefined);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const [feedsResult, clips, authors] = await Promise.all([
        getHomeShortsFeeds(),
        getAllMentorShorts(),
        getAllAuthors(),
      ]);
      setIsLoading(false);

      const authorNameById = new Map(authors.map((author) => [author.id, author.name]));
      setShorts(clips.map((clip) => ({ ...clip, authorName: authorNameById.get(clip.authorId) ?? '' })));

      const error = isProjectError(feedsResult);
      if (error) {
        showErrorToast(error);
        return;
      }
      const { cleaned, removed } = cleanFeeds(
        (feedsResult as HomeShortsFeeds | null) ?? EMPTY_FEEDS,
        new Set(clips.map((c) => c.id)),
      );
      setBaseline(cleaned);
      setDraft(cleaned);
      setRemovedCount(removed);
      setHasStaleIds(removed > 0);
    };
    load().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty = !!draft && !!baseline && (hasStaleIds || serialize(draft) !== serialize(baseline));

  // Warn before closing or reloading the tab with unsaved changes.
  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const shortsById = useMemo(() => new Map(shorts.map((short) => [short.id, short])), [shorts]);

  const shadowed = useMemo(() => getShadowedFeeds(draft?.feeds ?? []), [draft]);

  const activeIds = useMemo(() => new Set(shorts.filter((short) => short.isActive).map((short) => short.id)), [shorts]);

  // Active shorts that appear in no feed, default included.
  const unassignedIds = useMemo(() => {
    const assigned = new Set<string>(draft?.default.shortIds ?? []);
    draft?.feeds.forEach((feed) => feed.shortIds.forEach((id) => assigned.add(id)));
    return new Set(shorts.filter((short) => short.isActive && !assigned.has(short.id)).map((short) => short.id));
  }, [draft, shorts]);

  if (!draft) {
    return null;
  }

  // Falls back to Default when the selected feed no longer exists (e.g. after discard).
  const selectedFeed = draft.feeds.find((feed) => feed.id === selectedId);
  const isDefaultSelected = !selectedFeed;
  const selectedIds = selectedFeed ? selectedFeed.shortIds : draft.default.shortIds;
  const selectedName = selectedFeed
    ? selectedFeed.name
    : ENABLE_FEED_TARGETING
    ? 'Default (everyone else)'
    : 'Homepage order';

  const winnerId =
    previewLevel !== '' && previewAccess !== ''
      ? resolveFeed(draft, previewLevel, previewAccess, activeIds)?.id ?? DEFAULT_FEED_ID
      : undefined;

  const updateFeed = (id: string, changes: Partial<HomeShortsFeed>) =>
    setDraft({ ...draft, feeds: draft.feeds.map((feed) => (feed.id === id ? { ...feed, ...changes } : feed)) });

  const setSelectedIds = (ids: string[]) => {
    if (selectedFeed) {
      updateFeed(selectedFeed.id, { shortIds: ids });
    } else {
      setDraft({ ...draft, default: { shortIds: ids } });
    }
  };

  const onReorderFeeds = (orderedIds: string[]) => {
    const byId = new Map(draft.feeds.map((feed) => [feed.id, feed]));
    setDraft({
      ...draft,
      feeds: orderedIds.map((id) => byId.get(id)).filter((feed): feed is HomeShortsFeed => !!feed),
    });
  };

  const onSaveFeed = (values: FeedModalValues) => {
    if (feedModal?.feedId) {
      updateFeed(feedModal.feedId, { name: values.name, levels: values.levels, access: values.access });
    } else {
      const source =
        values.startFrom === DEFAULT_FEED_ID
          ? draft.default.shortIds
          : draft.feeds.find((feed) => feed.id === values.startFrom)?.shortIds ?? [];
      const newFeed: HomeShortsFeed = {
        id: createFeedId(),
        name: values.name,
        levels: values.levels,
        access: values.access,
        shortIds: [...source],
        isActive: true,
      };
      setDraft({ ...draft, feeds: [...draft.feeds, newFeed] });
      setSelectedId(newFeed.id);
    }
    setFeedModal(undefined);
  };

  const onDuplicateFeed = (id: string) => {
    const index = draft.feeds.findIndex((feed) => feed.id === id);
    if (index < 0) {
      return;
    }
    const source = draft.feeds[index];
    const copy: HomeShortsFeed = {
      ...source,
      id: createFeedId(),
      name: `${source.name} (copy)`,
      shortIds: [...source.shortIds],
    };
    const feeds = [...draft.feeds];
    feeds.splice(index + 1, 0, copy);
    setDraft({ ...draft, feeds });
    setSelectedId(copy.id);
  };

  const onConfirmDelete = () => {
    if (deleteId) {
      setDraft({ ...draft, feeds: draft.feeds.filter((feed) => feed.id !== deleteId) });
      if (selectedId === deleteId) {
        setSelectedId(DEFAULT_FEED_ID);
      }
    }
    setDeleteId(undefined);
  };

  const onAddShorts = (ids: string[], placement: AddPlacement) => {
    const existing = selectedIds.filter((id) => !ids.includes(id));
    if (placement.type === 'top') {
      setSelectedIds([...ids, ...existing]);
    } else if (placement.type === 'bottom') {
      setSelectedIds([...existing, ...ids]);
    } else {
      const next = [...existing];
      next.splice(Math.min(Math.max(placement.position - 1, 0), next.length), 0, ...ids);
      setSelectedIds(next);
    }
    showSuccessToast(`${ids.length} short${ids.length === 1 ? '' : 's'} added to the draft`);
  };

  const notes = draft.notes ?? {};

  // Merges imported notes into the draft, overwriting per short id.
  const onApplyImport = (ids: string[], importedNotes: Record<string, ShortNote>) => {
    const nextNotes = { ...notes, ...importedNotes };
    if (selectedFeed) {
      setDraft({
        ...draft,
        notes: nextNotes,
        feeds: draft.feeds.map((feed) => (feed.id === selectedFeed.id ? { ...feed, shortIds: ids } : feed)),
      });
    } else {
      setDraft({ ...draft, default: { shortIds: ids }, notes: nextNotes });
    }
  };

  // An empty note removes the entry.
  const onSaveNote = (id: string, note: ShortNote) => {
    const nextNotes = { ...notes };
    if (isNoteEmpty(note)) {
      delete nextNotes[id];
    } else {
      nextNotes[id] = { ...note, why: note.why.trim() };
    }
    setDraft({ ...draft, notes: nextNotes });
  };

  const onExport = () =>
    downloadCsv(
      `homepage-shorts-${slugify(selectedName)}.csv`,
      buildCsv(shortsToCsvRows(selectedIds, shortsById, notes)),
    );

  // Three real shorts when available, otherwise placeholder rows.
  const onDownloadExample = () => {
    const sample = shorts.slice(0, 3).map((short) => short.id);
    const rows = sample.length
      ? shortsToCsvRows(sample, shortsById, notes)
      : [
          ['1', 'SHORT_ID_1', 'Mentor name', 'Short title', '9.5', 'Why this short is worth ranking high', 'yes'],
          ['2', 'SHORT_ID_2', 'Mentor name', 'Short title', '7', '', ''],
          ['3', 'SHORT_ID_3', 'Mentor name', 'Short title', '', '', ''],
        ];
    downloadCsv(EXAMPLE_CSV_FILENAME, buildCsv(rows));
  };

  const onSave = async () => {
    setIsLoading(true);
    const result = await setHomeShortsFeeds(draft);
    setIsLoading(false);
    const error = isProjectError(result);
    if (error) {
      showErrorToast(error);
      return;
    }
    setBaseline(draft);
    setHasStaleIds(false);
    setRemovedCount(0);
    showSuccessToast('Homepage shorts saved');
  };

  const onDiscard = () => {
    if (baseline) {
      setDraft(baseline);
      setSelectedId(DEFAULT_FEED_ID);
    }
  };

  const editingFeed = feedModal?.feedId ? draft.feeds.find((feed) => feed.id === feedModal.feedId) : undefined;
  const deletingFeed = draft.feeds.find((feed) => feed.id === deleteId);

  return (
    <ScreenComponents.ComponentContainer style={{ gap: 16 }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant='body2' color='text.secondary' sx={{ flex: 1, minWidth: 240 }}>
          {ENABLE_FEED_TARGETING
            ? 'Rank the shorts shown on the app homepage for each user segment.'
            : 'Rank the shorts shown on the app homepage. Every user sees this order.'}{' '}
          Changes stay in a draft until you save.
        </Typography>
        {isDirty && <Chip size='small' color='warning' label='Unsaved changes' />}
        <Button onClick={onDiscard} disabled={!isDirty}>
          Discard
        </Button>
        <Button variant='contained' onClick={onSave} disabled={!isDirty}>
          Save changes
        </Button>
      </Box>

      {removedCount > 0 && (
        <Alert severity='info' sx={{ width: '100%' }}>
          {removedCount} short{removedCount === 1 ? '' : 's'} that no longer exist (or were repeated) were removed from
          the feeds. Save to apply this cleanup.
        </Alert>
      )}

      {unassignedIds.size > 0 && (
        <Alert
          severity='warning'
          sx={{ width: '100%' }}
          action={
            <Button color='inherit' size='small' onClick={() => setAddPanel({ onlyUnassigned: true })}>
              Review
            </Button>
          }>
          {unassignedIds.size} active short{unassignedIds.size === 1 ? ' is' : 's are'} not ranked
          {ENABLE_FEED_TARGETING ? ' in any feed (including Default)' : ''}. The app shows unranked shorts after the
          ranked ones, newest first.
        </Alert>
      )}

      <Components.Layout isSingle={!ENABLE_FEED_TARGETING}>
        {ENABLE_FEED_TARGETING && (
          <FeedList
            feeds={draft.feeds}
            defaultCount={draft.default.shortIds.length}
            selectedId={isDefaultSelected ? DEFAULT_FEED_ID : selectedId}
            levels={USER_LEVELS}
            winnerId={winnerId}
            shadowed={shadowed}
            previewLevel={previewLevel}
            previewAccess={previewAccess}
            onPreviewChange={(level, access) => {
              setPreviewLevel(level);
              setPreviewAccess(access);
            }}
            onSelect={setSelectedId}
            onReorder={onReorderFeeds}
            onToggleActive={(id) => updateFeed(id, { isActive: !draft.feeds.find((feed) => feed.id === id)?.isActive })}
            onNew={() => setFeedModal({})}
            onEdit={(id) => setFeedModal({ feedId: id })}
            onDuplicate={onDuplicateFeed}
            onDelete={setDeleteId}
          />
        )}
        <FeedEditor
          key={isDefaultSelected ? DEFAULT_FEED_ID : selectedId}
          title={selectedName}
          isDefault={isDefaultSelected}
          shortIds={selectedIds}
          shortsById={shortsById}
          notes={notes}
          onSaveNote={onSaveNote}
          onChange={setSelectedIds}
          onOpenAdd={() => setAddPanel({ onlyUnassigned: false })}
          onOpenImport={() => setIsImportOpen(true)}
          onExport={onExport}
          onDownloadExample={onDownloadExample}
          homepageSlots={HOMEPAGE_EXPERT_SLOTS}
        />
      </Components.Layout>

      <FeedModal
        open={!!feedModal}
        feed={editingFeed}
        levels={USER_LEVELS}
        otherFeeds={draft.feeds}
        onClose={() => setFeedModal(undefined)}
        onSave={onSaveFeed}
      />

      <AddShortsPanel
        open={!!addPanel}
        feedName={selectedName}
        shorts={shorts.filter((short) => !selectedIds.includes(short.id))}
        unassignedIds={unassignedIds}
        notes={notes}
        feedLength={selectedIds.length}
        initialOnlyUnassigned={!!addPanel?.onlyUnassigned}
        onClose={() => setAddPanel(undefined)}
        onAdd={onAddShorts}
      />

      <CsvImportModal
        open={isImportOpen}
        feedName={selectedName}
        currentIds={selectedIds}
        shortsById={shortsById}
        onClose={() => setIsImportOpen(false)}
        onApply={onApplyImport}
      />

      <Dialog open={!!deletingFeed} onClose={() => setDeleteId(undefined)}>
        <DialogTitle>Delete feed?</DialogTitle>
        <DialogContent>
          <DialogContentText>{deletingFeed?.name} will be removed once you save.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(undefined)}>Cancel</Button>
          <Button color='error' onClick={onConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ScreenComponents.ComponentContainer>
  );
};

export default HomepageShorts;
